import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import Video from '../Video/Video';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ComponentProps } from 'lib/component-props';
import CustomNextImage from '../CustomNextImage/CustomNextImage';
import SuccessPageMergeTag from '../../SitesSuccessPage/SuccessPageMergeTag/SuccessPageMergeTag';
import SuccessPageMergeTagText from '../../SitesSuccessPage/SuccessPageMergeTag/SuccessPageMergeTagText';
import UiText from '../Typography/UiText';
import UiHeading from '../Heading';
import Head from 'next/head';

type TextDetails = ComponentProps & {
  templateName: string;
  fields: {
    heading: Field<string>;
    content: Field<string>;
    fontColor: Field<string>;
  };
};

type ImageDetails = TextDetails & {
  fields: {
    imageContent?: ImageField;
    imageContentMobile: ImageField;
    imageContentTablet: ImageField;
    imageContentDesktop: ImageField;
  };
};

type VideoDetails = TextDetails & {
  fields: {
    videoUrl?: Field<string>;
    isAutoplay?: Field<boolean>;
    enableMute?: Field<boolean>;
    mobileHeight?: Field<string>;
    tabletHeight?: Field<string>;
    desktopHeight?: Field<string>;
    videoThumbnailURL?: Field<{ href: string }>;
    videoName?: Field<string>;
    videoDescription?: Field<string>;
    videoUploadDate?: Field<string>;
  };
};

type SingleColumn = {
  isSingleColumn: boolean;
};

type SiteInfo = {
  mergeTagData?: any;
};

type OneColumnProps = (TextDetails | ImageDetails | VideoDetails) & SingleColumn & SiteInfo;

const OneColumnGrid = (props: OneColumnProps): JSX.Element => {
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('lg'));
  if (!props?.fields) return <></>;
  const contentType = props?.templateName;
  const imageObjectFields = props as ImageDetails;
  const fontColor = props?.fields?.fontColor?.value;

  const generateVideoSchema = (videoProps: VideoDetails) => {
    const { videoUrl, videoName, videoDescription, videoThumbnailURL, videoUploadDate } =
      videoProps?.fields;

    if (!videoUrl?.value) {
      return null;
    }

    const videoSchema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: videoName?.value,
      description: videoDescription?.value,
      thumbnailUrl: videoThumbnailURL?.value?.href,
      uploadDate: videoUploadDate?.value?.split('T')[0],
      contentUrl: videoUrl?.value,
      embedUrl: videoUrl?.value,
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(videoSchema),
          }}
        />
      </Head>
    );
  };

  return (
    <Grid container item id="text-container">
      {contentType === 'TextContainer' && (
        <Grid container item gap={1}>
          <UiHeading size="h3" color="primary" styles={{ color: fontColor }} isChildren>
            <SuccessPageMergeTagText
              mergeTagField={props?.fields?.heading}
              mergeTagObj={props?.mergeTagData}
            />
          </UiHeading>
          <Typography variant="body" textAlign="left" color={'#000000'}>
            <UiText color="default" styles={{ fontFamily: 'inherit' }}>
              <SuccessPageMergeTag
                mergeTagField={props?.fields?.content}
                mergeTagObj={props?.mergeTagData}
              />
            </UiText>
          </Typography>
        </Grid>
      )}
      {contentType === 'VideoContainer' && (
        <Grid
          id="video-container"
          container
          item
          gap={1}
          alignItems="center"
          justifyContent="center"
          flexDirection={matches && !props?.isSingleColumn ? 'column-reverse' : 'column'}
        >
          {generateVideoSchema(props)}
          <Box sx={{ width: '100%' }}>
            <Video fields={props as VideoDetails}></Video>
          </Box>
          <Box>
            <Typography variant="btnText">
              <SuccessPageMergeTagText
                mergeTagField={props?.fields?.heading}
                mergeTagObj={props?.mergeTagData}
              />
            </Typography>
            <Typography variant="body" textAlign="justify">
              <SuccessPageMergeTag
                mergeTagField={props?.fields?.content}
                mergeTagObj={props?.mergeTagData}
              />
            </Typography>
          </Box>
        </Grid>
      )}
      {contentType === 'ImageContainer' && (
        <Grid
          id="image-container"
          container
          item
          gap={1}
          alignItems="center"
          justifyContent="center"
          flexDirection={matches && !props?.isSingleColumn ? 'column-reverse' : 'column'}
          sx={{ overflow: 'hidden', width: '100%' }}
        >
          <CustomNextImage
            mobileImg={imageObjectFields?.fields?.imageContentMobile}
            tabletImg={imageObjectFields?.fields?.imageContentTablet}
            desktopImg={imageObjectFields?.fields?.imageContentDesktop}
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
          <Box>
            <Typography variant="btnText">
              <SuccessPageMergeTagText
                mergeTagField={props?.fields?.heading}
                mergeTagObj={props?.mergeTagData}
              />
            </Typography>
            <Typography variant="body" textAlign="justify">
              <SuccessPageMergeTag
                mergeTagField={props?.fields?.content}
                mergeTagObj={props?.mergeTagData}
              />
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default OneColumnGrid;
